import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Clock, Award } from 'lucide-react';
import { Card, CardContent, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/Button';
import { courseService } from '../../services/course.service';
import type { MyCourse } from '@edytech/shared/types';

export const CoursesList: React.FC = () => {
  const { data: courses, isLoading } = useQuery<MyCourse[]>({
    queryKey: ['my-courses'],
    queryFn: () => courseService.getMyCourses(),
  });

  if (isLoading) {
    return <div className="py-16 text-center text-[rgb(var(--light-secondary))]">Loading your courses...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">My Courses</h1>
      <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">
        Continue learning and track your progress.
      </p>

      {!courses?.length ? (
        <div className="mt-8 rounded-xl border border-dashed border-[rgb(var(--border))] p-12 text-center text-[rgb(var(--light-secondary))]">
          <BookOpen className="mx-auto h-12 w-12 opacity-40" />
          <p className="mt-3">You haven't enrolled in any courses yet.</p>
          <Link to="/courses" className="mt-4 inline-block">
            <Button>Browse Courses</Button>
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {courses.map((course) => {
            const isComplete = course.status === 'COMPLETED';
            const resumePath = `/learn/${course.courseSlug}/lessons/${course.lastAccessedLessonId || 0}`;
            return (
              <Card key={course.id} className="flex flex-col">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  {isComplete ? <Award className="h-6 w-6" /> : <BookOpen className="h-6 w-6" />}
                </div>
                <CardTitle>{course.courseTitle}</CardTitle>
                <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">{course.level}</p>

                <div className="mt-4 flex items-center space-x-2 text-sm text-[rgb(var(--light-secondary))]">
                  <Clock className="h-4 w-4" />
                  <span>
                    {course.completedLessons}/{course.totalLessons} lessons
                  </span>
                </div>

                <div className="mt-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[rgb(var(--light-secondary))]">Progress</span>
                    <span className="font-medium">{course.progressPercent}%</span>
                  </div>
                  <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-[rgb(var(--border))]">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${course.progressPercent}%` }} />
                  </div>
                </div>

                <CardContent className="mt-4 flex items-center">
                  {isComplete ? (
                    <span className="inline-flex items-center space-x-1 text-sm font-medium text-green-600">
                      <Award className="h-4 w-4" /> Completed
                    </span>
                  ) : (
                    <Link to={resumePath} className="text-sm font-medium text-primary hover:underline">
                      Continue &rarr;
                    </Link>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
